import { useState, useMemo } from 'react';
import { format, subDays, startOfMonth, startOfYear, endOfMonth, parseISO } from 'date-fns';
import { Input } from '../ui/input';
import { CalendarRange } from 'lucide-react';

/**
 * Compact pill-cluster date-range filter used by both Manager Customer
 * Feedback and Admin Customer Experience screens.
 *
 * Presets: Today / Yesterday / MTD / YTD / Month-picker / Custom.
 * Emits `{ start_date, end_date, label }` via `onChange` whenever the
 * selection changes. Keeps state local — parent persists the chosen range.
 *
 * Visual language mirrors the existing dashboard filter pill clusters
 * (search/category in PricingTab, range chips in AdminInsights).
 */
export default function DateRangeFilter({ value, onChange, testId = 'cx-date-range-filter' }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
  const mtdStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const ytdStart = format(startOfYear(new Date()), 'yyyy-MM-dd');

  const presets = useMemo(() => ([
    { key: 'today',    label: 'Today',     start: today,     end: today },
    { key: 'yesterday', label: 'Yesterday', start: yesterday, end: yesterday },
    { key: 'mtd',      label: 'MTD',       start: mtdStart,  end: today },
    { key: 'ytd',      label: 'YTD',       start: ytdStart,  end: today },
  ]), [today, yesterday, mtdStart, ytdStart]);

  const active = value || { key: 'mtd', start: mtdStart, end: today, label: 'MTD' };
  const [pickerOpen, setPickerOpen] = useState(false);
  const [monthVal, setMonthVal] = useState(format(new Date(), 'yyyy-MM'));
  const [customStart, setCustomStart] = useState(active.start);
  const [customEnd, setCustomEnd] = useState(active.end);

  const apply = (preset) => {
    onChange?.({ key: preset.key, start_date: preset.start, end_date: preset.end, label: preset.label });
    setPickerOpen(false);
  };

  const applyMonth = (m) => {
    if (!m) return;
    setMonthVal(m);
    const dt = parseISO(`${m}-01`);
    const start = format(startOfMonth(dt), 'yyyy-MM-dd');
    const end = format(endOfMonth(dt), 'yyyy-MM-dd');
    onChange?.({ key: `month-${m}`, start_date: start, end_date: end, label: format(dt, 'MMM yyyy') });
  };

  const applyCustom = () => {
    if (!customStart || !customEnd) return;
    if (customEnd < customStart) return;
    onChange?.({
      key: `custom-${customStart}-${customEnd}`,
      start_date: customStart,
      end_date: customEnd,
      label: `${customStart} → ${customEnd}`,
    });
  };

  return (
    <div className="inline-flex items-center gap-2 flex-wrap" data-testid={testId}>
      <div className="inline-flex items-center gap-0 p-0.5 rounded-md bg-[#F3F5F2] border border-[#E2E8DF]">
        {presets.map(p => (
          <button key={p.key} type="button"
            onClick={() => apply(p)}
            data-testid={`${testId}-preset-${p.key}`}
            className={`h-6 px-2.5 rounded text-[10px] font-semibold transition-colors ${active.key === p.key ? 'bg-white shadow-sm text-[color:var(--color-primary)]' : 'text-[#5C6B64] hover:text-[#1A2421]'}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Month picker */}
      <div className="inline-flex items-center gap-1 px-2 h-7 rounded-md border border-[#E2E8DF] bg-white">
        <CalendarRange className="w-3 h-3 text-[#5C6B64]" />
        <Input type="month"
          value={monthVal}
          onChange={(e) => applyMonth(e.target.value)}
          data-testid={`${testId}-month`}
          className="h-5 w-[120px] text-[10px] tabular-nums px-1 border-0 focus-visible:ring-0"
        />
      </div>

      {/* Custom range — collapsible to keep the strip slim */}
      <button type="button"
        onClick={() => setPickerOpen(o => !o)}
        data-testid={`${testId}-custom-toggle`}
        className={`h-7 px-2.5 rounded-md text-[10px] font-semibold border transition-colors ${pickerOpen || active.key.startsWith('custom-') ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)]' : 'bg-white text-[color:var(--color-primary)] border-[#E2E8DF] hover:bg-[#F3F5F2]'}`}>
        Custom
      </button>
      {pickerOpen && (
        <div className="inline-flex items-center gap-1.5">
          <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
            data-testid={`${testId}-custom-start`} className="h-7 w-[140px] text-[11px] tabular-nums px-2" />
          <span className="text-[10px] text-[#5C6B64]">→</span>
          <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
            data-testid={`${testId}-custom-end`} className="h-7 w-[140px] text-[11px] tabular-nums px-2" />
          <button type="button" onClick={applyCustom}
            data-testid={`${testId}-custom-apply`}
            className="h-7 px-2.5 rounded-md bg-[color:var(--color-primary)] text-white text-[10px] font-semibold hover:bg-[#0F2A20]">
            Apply
          </button>
        </div>
      )}

      <span className="text-[10px] text-[#5C6B64] tabular-nums" data-testid={`${testId}-active-label`}>
        {active.label || `${active.start} → ${active.end}`}
      </span>
    </div>
  );
}
